import GeneralHeroSection from "../educate/generalHeroSection";
import NoImageSectionCard from "../general/noImageSectionCard";

const differentiators = [
    {
        label: "Startup Trainers",
        heading: "Not instructors. Founders.",
        body: "Every team is led by a Startup Trainer — a vetted founder who has raised capital and shipped a real product to real users. They run two structured standups per week, stay available on Slack throughout the program, and write your letter of recommendation at the end. You're not learning from someone who teaches about startups. You're working with someone who runs one.",
    },
    {
        label: "The EA Guarantee",
        heading: "Your product stays live. For a year.",
        body: "Every product built at Extern Academy goes live. Not as a prototype, not as a demo — as a deployed consumer product. And we guarantee it stays online for a full year after launch. That means when you're applying to colleges or interviewing for internships six months later, you can still pull it up. It still points to you.",
    },
    {
        label: "Real Ownership",
        heading: "A C-suite title. Earned, not assigned.",
        body: "At Extern Academy, every extern holds a real executive role — CEO, CTO, CMO, CPO, COO, or CGO. You own your domain from day one to launch. You make the decisions, take the accountability, and own the outcomes. That's a kind of responsibility most people don't experience until their late twenties.",
    },
    {
        label: "Your First Network",
        heading: "Build with people who are building.",
        body: "The people you build with at Extern Academy aren't classmates filling a group project. They're future founders, engineers, and operators — and you'll have already shipped something together. The cohort stays connected. Those relationships compound over time.",
    },
];

const programs = [
    {
        days: "14 Days",
        name: "2027 Startup Sprint",
        date: "January 25, 2027",
        pace: "5–10 hrs/week",
        desc: "The fastest path to a live product. You join a small founding team, own a real role, and launch a consumer product in 14 days. A focused, high-intensity sprint from kickoff to launch.",
        href: "/admissions/2027-startup-sprint",
    },
    {
        days: "28 Days",
        name: "Founder Track",
        date: "February 2027",
        pace: "8–12 hrs/week",
        desc: "A full build cycle. More time to go deeper — more iteration, more refinement, more ownership. You hold your C-suite title for 28 days and take the product further.",
        href: "/admissions/founder-track",
    },
    {
        days: "48 Days",
        name: "The Residency",
        date: "May 2027",
        pace: "Intensive",
        desc: "The most demanding program we offer. You take a company from discovery all the way through growth. 20 seats. The highest bar. For the students who want the full founder experience.",
        href: "/admissions/residency",
    },
];

export default function AboutHelperComponent() {
    return (
        <div className="layout-container d-flex flex-column min-vh-100 overflow-x-hidden">

            <GeneralHeroSection
                title="About Extern Academy"
                blurb="We exist for the builders."
                imageURL="/about-page.webp"
                imageALT="Extern Academy — students building real products"
            />

            <main role="main" className="flex-grow-1">
                <a id="main-content" tabIndex="-1"></a>

                {/* What we are */}
                <NoImageSectionCard
                    title="What is Extern Academy?"
                    blurb={
                        <div className="space-y-4 text-left">
                            <p>
                                Extern Academy is a startup program for ambitious high school students. Not a class. Not a camp. A structured sprint where you join a small founding team, own a real executive role, and ship a live consumer product — guided by a founder who has actually done it.
                            </p>
                            <p>
                                We run three programs: a 14-day Startup Sprint, a 28-day Founder Track, and a 48-day Residency. Each one ends the same way: a product goes live. Your name is on it.
                            </p>
                        </div>
                    }
                />

                {/* Why we exist */}
                <div style={{ backgroundColor: "#f3f4f6" }} className="py-5">
                    <div className="container py-4">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-8">
                                <p
                                    className="fw-semibold text-uppercase mb-3"
                                    style={{ color: "#3769B9", letterSpacing: "0.08em", fontSize: "0.82rem" }}
                                >
                                    Why We Exist
                                </p>
                                <h2 className="fw-bold mb-4" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", lineHeight: 1.2 }}>
                                    Traditional education treats students like passengers.
                                </h2>
                                <div className="space-y-4" style={{ color: "#444", fontSize: "clamp(1rem, 1.8vw, 1.1rem)", lineHeight: 1.8 }}>
                                    <p>
                                        Show up. Listen. Take notes. Repeat. You graduate with a GPA and a transcript that looks exactly like everyone else's.
                                    </p>
                                    <p>
                                        <strong style={{ color: "#111" }}>That's broken.</strong>
                                    </p>
                                    <p>
                                        Admissions committees and first employers aren't counting credentials anymore — they have too many. They're looking for one thing: evidence that you can actually <em>do</em> something. Build something. Lead something. Ship something under a deadline.
                                    </p>
                                    <p>
                                        Extern Academy exists to give ambitious students that proof. Not a certificate from a camp. Not a line on a resume. A live product. A real role you owned. A letter of recommendation from a founder who watched you build it.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* How it works */}
                <div className="py-5">
                    <div className="container py-4">
                        <div className="row justify-content-center mb-5">
                            <div className="col-12 col-lg-8 text-center">
                                <p
                                    className="fw-semibold text-uppercase mb-3"
                                    style={{ color: "#3769B9", letterSpacing: "0.08em", fontSize: "0.82rem" }}
                                >
                                    The Model
                                </p>
                                <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)" }}>
                                    Small teams. Real roles. Hard deadlines.
                                </h2>
                                <p className="text-muted" style={{ fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)", lineHeight: 1.7 }}>
                                    Every extern joins a founding team of 4–6 students and owns one executive function — CEO, CTO, CMO, CPO, COO, or CGO. Two structured standups per week. Async work in between. A hard launch date at the end.
                                </p>
                            </div>
                        </div>

                        {/* Step cards */}
                        <div className="row g-3 justify-content-center">
                            {[
                                { step: "01", title: "You're placed on a team", body: "Small teams of 4–6 students, each with a different role. No two people own the same function." },
                                { step: "02", title: "You own your domain", body: "From day one, your role is yours. The decisions in your lane belong to you — and so do the outcomes." },
                                { step: "03", title: "You work with a real founder", body: "Your Startup Trainer runs two standups per week and stays available throughout. They've raised capital and shipped to real users." },
                                { step: "04", title: "You launch something real", body: "The program ends with a live consumer product. It goes online. It stays there for a year. Your name is on it." },
                            ].map(({ step, title, body }) => (
                                <div key={step} className="col-12 col-md-6 col-lg-3">
                                    <div
                                        className="h-100 rounded-3 p-4 bg-white"
                                        style={{ border: "1.5px solid #e5e7eb" }}
                                    >
                                        <p
                                            className="fw-bold mb-3"
                                            style={{ color: "#3769B9", fontSize: "1.5rem", lineHeight: 1 }}
                                        >
                                            {step}
                                        </p>
                                        <p className="fw-bold mb-2" style={{ fontSize: "0.95rem", color: "#111" }}>{title}</p>
                                        <p className="text-muted mb-0" style={{ fontSize: "0.87rem", lineHeight: 1.65 }}>{body}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Programs */}
                <div style={{ backgroundColor: "#f3f4f6" }} className="py-5">
                    <div className="container py-4">
                        <div className="row justify-content-center mb-5">
                            <div className="col-12 col-lg-8 text-center">
                                <p
                                    className="fw-semibold text-uppercase mb-3"
                                    style={{ color: "#3769B9", letterSpacing: "0.08em", fontSize: "0.82rem" }}
                                >
                                    Programs
                                </p>
                                <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)" }}>
                                    Three programs. One standard.
                                </h2>
                                <p className="text-muted" style={{ fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)" }}>
                                    Every program ends with a live product. They differ in depth, pace, and duration.
                                </p>
                            </div>
                        </div>
                        <div className="row g-4">
                            {programs.map(({ days, name, date, pace, desc, href }) => (
                                <div key={href} className="col-12 col-lg-4">
                                    <a href={href} className="text-decoration-none h-100 d-block">
                                        <div
                                            className="h-100 bg-white rounded-3 p-4 d-flex flex-column"
                                            style={{ border: "1.5px solid #e5e7eb", transition: "border-color 0.15s" }}
                                            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#3769B9")}
                                            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
                                        >
                                            <p
                                                className="fw-semibold text-uppercase mb-2"
                                                style={{ color: "#3769B9", fontSize: "0.72rem", letterSpacing: "0.08em" }}
                                            >
                                                {days} · {pace}
                                            </p>
                                            <h3 className="fw-bold mb-1" style={{ fontSize: "1.15rem", color: "#111" }}>{name}</h3>
                                            <p className="mb-3" style={{ fontSize: "0.8rem", color: "#888" }}>{date}</p>
                                            <p className="text-muted flex-grow-1 mb-4" style={{ fontSize: "0.88rem", lineHeight: 1.65 }}>{desc}</p>
                                            <span
                                                className="d-inline-flex align-items-center gap-1 fw-semibold"
                                                style={{ color: "#3769B9", fontSize: "0.88rem" }}
                                            >
                                                Learn more{" "}
                                                <span className="material-symbols-outlined" style={{ fontSize: "1rem" }}>arrow_forward</span>
                                            </span>
                                        </div>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Differentiators */}
                <div className="py-5">
                    <div className="container py-4">
                        <div className="row justify-content-center mb-5">
                            <div className="col-12 col-lg-8 text-center">
                                <p
                                    className="fw-semibold text-uppercase mb-3"
                                    style={{ color: "#3769B9", letterSpacing: "0.08em", fontSize: "0.82rem" }}
                                >
                                    What Makes It Real
                                </p>
                                <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)" }}>
                                    This isn't a simulation.
                                </h2>
                                <p className="text-muted" style={{ fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)" }}>
                                    The things that make Extern Academy different aren't features. They're the structure that makes real outcomes possible.
                                </p>
                            </div>
                        </div>
                        <div className="row g-4">
                            {differentiators.map(({ label, heading, body }) => (
                                <div key={label} className="col-12 col-md-6">
                                    <div
                                        className="h-100 rounded-3 p-5"
                                        style={{ backgroundColor: "#f9fafb", border: "1.5px solid #e5e7eb" }}
                                    >
                                        <p
                                            className="fw-semibold text-uppercase mb-2"
                                            style={{ color: "#3769B9", fontSize: "0.72rem", letterSpacing: "0.08em" }}
                                        >
                                            {label}
                                        </p>
                                        <h3 className="fw-bold mb-3" style={{ fontSize: "clamp(1.1rem, 2vw, 1.35rem)", color: "#111" }}>
                                            {heading}
                                        </h3>
                                        <p className="text-muted mb-0" style={{ fontSize: "0.9rem", lineHeight: 1.75 }}>
                                            {body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="py-5" style={{ backgroundColor: "#3769B9" }}>
                    <div className="container text-center text-white py-3">
                        <h2 className="fw-bold mb-3" style={{ fontSize: "clamp(1.4rem, 3vw, 2rem)" }}>
                            Ready to build something real?
                        </h2>
                        <p
                            className="mb-4 mx-auto"
                            style={{ opacity: 0.8, fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)", maxWidth: "440px" }}
                        >
                            The application is free and takes 30 minutes. Cohorts are small — spots fill fast.
                        </p>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            <a href="/apply" className="btn btn-lg btn-danger d-inline-flex align-items-center gap-2">
                                Apply Now <span className="material-symbols-outlined">arrow_forward</span>
                            </a>
                            <a href="/admissions" className="btn btn-lg btn-secondary d-inline-flex align-items-center gap-2">
                                See Programs
                            </a>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}
